import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { vehicleService } from "@/services/VehicleService";
import { VehicleDetailModel, VehicleTypes } from "@/models/vehicle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AddVehicleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: number;
  /** Drives the title/label wording. */
  mode: "add" | "change";
  /** The application's current vehicle, pre-loaded so mileage can be updated without re-searching. */
  initialVehicle?: VehicleDetailModel | null;
  onConfirm: (vehicle: VehicleDetailModel) => void;
}

const EDITABLE_VEHICLE_TYPES = [
  VehicleTypes.Car,
  VehicleTypes.Caravan,
  VehicleTypes.Motorbike,
  VehicleTypes.Van,
] as const;
const VEHICLE_DETAIL_ROW_CLASS =
  "grid min-h-12 grid-cols-[minmax(0,1fr)_8rem] items-center gap-4 border-b border-border py-1.5 last:border-0";

function getEditableVehicleType(vehicleType: VehicleTypes | null | undefined): VehicleTypes {
  return EDITABLE_VEHICLE_TYPES.includes(
    vehicleType as (typeof EDITABLE_VEHICLE_TYPES)[number]
  )
    ? vehicleType as VehicleTypes
    : VehicleTypes.Car;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={VEHICLE_DETAIL_ROW_CLASS}>
      <span className="min-w-0 text-[12px] text-muted-foreground">{label}</span>
      <span className="w-full text-right text-[12px] font-medium text-foreground">{value}</span>
    </div>
  );
}

export function AddVehicleModal({ open, onOpenChange, applicationId, mode, initialVehicle, onConfirm }: AddVehicleModalProps) {
  const [vrm, setVrm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<VehicleDetailModel | null>(null);
  const [mileage, setMileage] = useState("");

  // Seed the form with the current vehicle when the dialog opens so the agent
  // can update mileage without having to search again.
  useEffect(() => {
    if (open && initialVehicle) {
      setVehicle(initialVehicle);
      setVrm(initialVehicle.regNumber ?? "");
      setMileage(initialVehicle.mileage != null ? String(initialVehicle.mileage) : "");
    }
  }, [open, initialVehicle]);

  const reset = () => {
    setVrm("");
    setLoading(false);
    setError(null);
    setVehicle(null);
    setMileage("");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSearch = async () => {
    const registration = vrm.trim();
    if (!registration) return;
    setLoading(true);
    setError(null);
    setVehicle(null);
    try {
      const result = await vehicleService.vehicleSearchByVRM(registration, applicationId);
      setVehicle(result);
      setMileage(result.mileage != null ? String(result.mileage) : "");
    } catch {
      setError("No vehicle found for that registration. Please check the VRM and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!vehicle) return;
    const parsedMileage = mileage.trim() === "" ? null : Number(mileage.replace(/[^0-9]/g, ""));
    onConfirm({
      ...vehicle,
      mileage: parsedMileage,
      vehicleType: getEditableVehicleType(vehicle.vehicleType),
    });
    reset();
    onOpenChange(false);
  };

  const title = mode === "change" ? "Change Vehicle" : "Add Vehicle";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Enter the vehicle registration to look up its details.</DialogDescription>
        </DialogHeader>

        {/* VRM search */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              className="pl-8 uppercase"
              placeholder="e.g. AB21 CDE"
              value={vrm}
              onChange={(e) => setVrm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
          </div>
          <Button onClick={handleSearch} disabled={loading || !vrm.trim()}>
            {loading ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>

        {error && <p className="text-sm font-medium text-rose-600">{error}</p>}

        {/* Result */}
        {vehicle && (
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
            <DetailRow label="Make" value={vehicle.make || "—"} />
            <DetailRow label="Model" value={vehicle.model || "—"} />
            <div className={VEHICLE_DETAIL_ROW_CLASS}>
              <span className="min-w-0 text-[12px] text-muted-foreground">Vehicle type</span>
              <Select
                value={String(getEditableVehicleType(vehicle.vehicleType))}
                onValueChange={(value) =>
                  setVehicle((current) =>
                    current ? { ...current, vehicleType: Number(value) as VehicleTypes } : current
                  )
                }
              >
                <SelectTrigger aria-label="Vehicle type" className="h-8 w-full text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EDITABLE_VEHICLE_TYPES.map((vehicleType) => (
                    <SelectItem key={vehicleType} value={String(vehicleType)}>
                      {VehicleTypes[vehicleType]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={VEHICLE_DETAIL_ROW_CLASS}>
              <span className="min-w-0 text-[12px] text-muted-foreground">Mileage</span>
              <div className="relative w-full">
                <Input
                  aria-label="Mileage"
                  inputMode="numeric"
                  className="h-8 w-full pr-8 text-right text-[12px] md:text-[12px]"
                  placeholder="0"
                  value={mileage}
                  onChange={(e) => setMileage(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-muted-foreground">
                  mi
                </span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-3 sm:gap-3">
          <DialogClose asChild>
            <Button variant="outline" disabled={loading}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            className="bg-purple-600 text-white hover:bg-purple-700"
            onClick={handleConfirm}
            disabled={!vehicle || loading}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
